import { useState, useEffect, useMemo } from "react";
import { filterEligibleListings } from "../../utils/openHouseHelpers";
import { resolveOpportunityApprovers } from "../../utils/openHouseOpportunityHelpers";
import {
  createOpportunity,
  getEligibleListingsForOpenHouse,
} from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";

export default function PostUpForGrabsModal({ open, onClose, lockListing, onPosted }) {
  const [{ user, users }] = useStateValue();
  const [eligibleDeals, setEligibleDeals] = useState([]);
  const [dealId, setDealId] = useState("");
  const [suggestedTiming, setSuggestedTiming] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSuggestedTiming("");
    if (lockListing) {
      setDealId(lockListing.id);
      return;
    }
    setDealId("");
    getEligibleListingsForOpenHouse().then((deals) => {
      const eligible = filterEligibleListings(deals).sort((a, b) =>
        (a.propertyaddress || "").localeCompare(b.propertyaddress || ""),
      );
      setEligibleDeals(eligible);
    });
  }, [open, lockListing]);

  const selectedDeal = useMemo(
    () => lockListing || eligibleDeals.find((d) => d.id === dealId),
    [lockListing, eligibleDeals, dealId],
  );

  if (!open) return null;

  const handlePost = async () => {
    if (!selectedDeal) {
      toast.error("Please choose a listing.");
      return;
    }
    setSaving(true);
    try {
      const approvers = resolveOpportunityApprovers(selectedDeal, users);
      // Single resolved LC → store it for routing; otherwise leave blank so the
      // approval falls to the shared Admin/Supervisor queue.
      const approverLcId = approvers.length === 1 ? approvers[0] : "";
      await createOpportunity({
        deal: selectedDeal,
        suggestedTiming,
        approverLcId,
        userId: user.id,
        userName: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
      });
      toast.success("Posted to the Up For Grabs board.");
      onPosted && onPosted();
      onClose();
    } catch (err) {
      console.error("Post up-for-grabs failed:", err);
      toast.error("Could not post. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/55 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Post Up For Grabs</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Listing</label>
            <select
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              disabled={!!lockListing}
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm disabled:opacity-70"
            >
              <option value="">— Select a listing —</option>
              {lockListing && (
                <option value={lockListing.id}>
                  {lockListing.propertyaddress || lockListing.id}
                </option>
              )}
              {!lockListing &&
                eligibleDeals.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.propertyaddress || d.id}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Suggested Timing (Optional)
            </label>
            <input
              value={suggestedTiming}
              onChange={(e) => setSuggestedTiming(e.target.value)}
              placeholder="e.g. Ideally this weekend — or leave blank for any time"
              className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handlePost}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-slate-800 hover:bg-slate-900 rounded-md disabled:opacity-50"
          >
            {saving ? "Posting…" : "Post Up For Grabs"}
          </button>
        </div>
      </div>
    </div>
  );
}
